import type { FC } from "react";
import { useCallback, useMemo } from "react";

import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuItem, SubMenu } from "mds/components/Menu";
import { AddPlusIcon, CopySendArrowIcon, CrossRemoveIcon, PersonGroupIcon } from "mds/icons";
import { storeApi } from "store/index";
import { NestedPageGroupType } from "store/selectors";
import { pageGroupUserSelectors } from "store/selectors/base";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher } from "utils/alerts";

interface StudentOptionsMenuProps {
  studentSectionUserId: string;
  isInGroup?: boolean;
  currentGroupId?: string;
  page: PageType;
  pageGroups: NestedPageGroupType[];
  onRemove?: (studentSectionUserId: string) => void;
  isReadOnly?: boolean;
}

export const StudentOptionsMenu: FC<StudentOptionsMenuProps> = ({
  studentSectionUserId,
  isInGroup = false,
  currentGroupId,
  page,
  pageGroups,
  onRemove,
  isReadOnly = false,
}) => {
  const allPageGroupUsers = useListSelector(pageGroupUserSelectors.selectAll);

  // Find the current page group user record for this student in this group
  const currentPageGroupUser = allPageGroupUsers.find(
    (pgu) => pgu.section_user_id === studentSectionUserId && pgu.page_group_id === currentGroupId,
  );

  const onRemoveFromGroup = async () => {
    if (!currentPageGroupUser) {
      return;
    }
    try {
      await storeApi.page_group_users.destroy(currentPageGroupUser.id);
      onRemove?.(studentSectionUserId);
    } catch (e) {
      toastLocalizedOperationCatcher("failed_to_remove_from_group")(e);
    }
  };

  // When in ungrouped list (currentGroupId undefined), student may still have a PageGroupUser on this
  // page (e.g. is_ready: false). We update that record instead of creating a duplicate.
  const pguToUpdate = useMemo(() => {
    if (currentPageGroupUser) return currentPageGroupUser;
    if (currentGroupId !== undefined) return null;
    return (
      pageGroups
        .flatMap((pg) => pg.page_group_users)
        .find((pgu) => pgu.section_user_id === studentSectionUserId) ?? null
    );
  }, [currentGroupId, currentPageGroupUser, pageGroups, studentSectionUserId]);

  const moveStudentToGroup = useCallback(
    (targetGroupId: string) => {
      const payload = {
        page_group_id: targetGroupId,
        section_user_id: studentSectionUserId,
        is_ready: true,
      };
      if (pguToUpdate) {
        return storeApi.page_group_users.partial_update({ id: pguToUpdate.id, ...payload });
      }
      return storeApi.page_group_users.create({ section_id: page.section_id, ...payload });
    },
    [page.section_id, pguToUpdate, studentSectionUserId],
  );

  const onMoveToGroup = async (targetGroupId: string) => {
    try {
      await moveStudentToGroup(targetGroupId);
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_move_to_group")(error);
    }
  };

  const onCreateNewGroup = async () => {
    try {
      const newGroup = await storeApi.page_groups.create({
        page_id: page.id,
        section_id: page.section_id,
      });
      await moveStudentToGroup(newGroup.id);
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_create_new_group")(error);
    }
  };

  if (isReadOnly) {
    return null;
  }

  return (
    <SidebarOptionsMenu>
      {isInGroup && currentPageGroupUser && (
        <MenuItem onClick={onRemoveFromGroup}>
          <IconText
            iconStart={<CrossRemoveIcon />}
            text={t("group_configuration_card.remove_from_group")}
          />
        </MenuItem>
      )}

      <SubMenu
        label={
          <IconText
            iconStart={<CopySendArrowIcon />}
            text={t("group_configuration_card.move_to_group")}
          />
        }
        overflow="auto"
      >
        {pageGroups.map((pageGroup, index) => {
          const isCurrentGroup = pageGroup.id === currentGroupId;
          const groupNumber = index + 1;

          return (
            <MenuItem
              disabled={isCurrentGroup}
              key={pageGroup.id}
              onClick={() => onMoveToGroup(pageGroup.id)}
            >
              <IconText
                iconStart={<PersonGroupIcon />}
                text={t("common.group_title", { number: groupNumber })}
              />
            </MenuItem>
          );
        })}

        <MenuItem onClick={onCreateNewGroup}>
          <IconText iconStart={<AddPlusIcon />} text={t("group_configuration_card.new_group")} />
        </MenuItem>
      </SubMenu>
    </SidebarOptionsMenu>
  );
};
